import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import SaveButtonComponent from '../utilsComponents/SaveButtonComponent';
import { RemoveRowButtonComponent } from '../utilsComponents/RemoveRowButtonComponent';
import LoadingIconComponent from '../iconsComponents/LoadingIconComponent';
import { useGetForbiddenMlCategoriesManual } from 'hooks/useGetForbiddenMlCategories';

const mergeForbiddenMlCategories = (defaultCategories, savedCategories = []) => {
  const savedById = Object.fromEntries(
    savedCategories.map((category) => [category.id, category])
  );
  const defaultIds = new Set(defaultCategories.map((category) => category.id));

  const mergedDefaults = defaultCategories.map((category) => ({
    ...category,
    enable: savedById[category.id]?.enable ?? category.enable ?? 0,
    ...(savedById[category.id]?.byuser
      ? { byuser: savedById[category.id].byuser }
      : {}),
  }));

  const userAdded = savedCategories.filter(
    (category) => !defaultIds.has(category.id)
  );

  return [...mergedDefaults, ...userAdded];
};

const ForbiddenMlCategories = ({
  forbiddenMlCategories,
  setForbiddenMlCategories,
  savedForbiddenMlCategories,
  onSaveSettings,
  isValidated,
  isValidatedKg,
  loading,
}) => {
  const [finder, setFinder] = useState('');
  const [visibleForbiddenCategories, setVisibleForbiddenCategories] = useState(
    forbiddenMlCategories
  );
  const [updatingList, setUpdatingList] = useState(false);
  const { getForbiddenMlCategories } = useGetForbiddenMlCategoriesManual();

  useEffect(() => {
    const filteredCategories = forbiddenMlCategories
      .filter(
        (item) =>
          item.id?.toLowerCase().includes(finder.toLowerCase()) ||
          item.name?.toLowerCase().includes(finder.toLowerCase())
      )
      .slice()
      .sort((a, b) => b.enable - a.enable);

    setVisibleForbiddenCategories(filteredCategories);
  }, [forbiddenMlCategories, finder]);

  const handlerOnChangeFinder = (value) => {
    setFinder(value);
  };

  const onRemoveRow = (position, item) => {
    const newForbiddenCategories = forbiddenMlCategories.filter(
      (category) => category.id !== item.id
    );
    setFinder('');
    setForbiddenMlCategories(newForbiddenCategories);
  };

  const forbiddenCategoryOptionHandler = (itemId, value) => {
    const valueInt = +value;
    const item = forbiddenMlCategories.find((category) => category.id === itemId);

    if (!item || item.enable === valueInt) return;

    const updatedCategories = forbiddenMlCategories.map((category) =>
      category.id === itemId ? { ...category, enable: valueInt } : category
    );

    setForbiddenMlCategories(updatedCategories);
  };

  const addToForbiddenCategoriesHandler = () => {
    setForbiddenMlCategories([
      ...forbiddenMlCategories,
      { id: finder.trim(), enable: 1, byuser: 1 },
    ]);
    setFinder('');
  };

  const handleUpdateList = async () => {
    setUpdatingList(true);

    try {
      const response = await getForbiddenMlCategories();
      const defaults =
        response?.data?.getForbiddenMlCategories ??
        response?.getForbiddenMlCategories;

      if (!defaults?.length) return;

      const defaultCategories = defaults.map(({ id }) => ({
        id,
        enable: 0,
      }));

      const savedCategories =
        savedForbiddenMlCategories !== null
          ? savedForbiddenMlCategories
          : forbiddenMlCategories;

      setForbiddenMlCategories(
        mergeForbiddenMlCategories(defaultCategories, savedCategories)
      );
    } finally {
      setUpdatingList(false);
    }
  };

  return (
    <div className='flex flex-col justify-between h-full'>
      <div className='w-full flex items-center'>
        <input
          type='text'
          name='forbiddenCategoriesFinder'
          id='forbiddenCategoriesFinder'
          value={finder}
          placeholder='Buscar...'
          className='block w-[30%] rounded-md border-0 border-gray-300 py-1.5 text-gray-900 shadow ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:outline-none focus:ring-1 sm:text-sm sm:leading-6 pl-2'
          onChange={(e) => handlerOnChangeFinder(e.target.value)}
        />
        <button
          type='button'
          className='ml-4 min-w-[128px] bg-primary-100 border border-primary-100 hover:border-primary-500 text-primary-700 inline-flex items-center justify-center px-4 py-2 font-semibold leading-6 text-sm shadow rounded-md transition ease-in-out duration-150 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:border-gray-200 disabled:text-gray-300'
          onClick={handleUpdateList}
          disabled={updatingList}
        >
          {!updatingList ? (
            <span className='inline-flex items-center justify-center [&_svg]:ml-0 [&_svg]:mr-0'>
              <LoadingIconComponent />
            </span>
          ) : (
            'Actualizar lista'
          )}
        </button>
        {visibleForbiddenCategories.length === 0 && finder.trim() && (
          <div
            className='flex items-center cursor-pointer text-primary-700 font-semibold ml-4'
            onClick={addToForbiddenCategoriesHandler}
            aria-hidden='true'
          >
            Agregar
          </div>
        )}
      </div>
      <div className='w-full flex max-h-[580px] h-[85%]'>
        <div className='w-[50%] border-2 border-r-primary-100 border-x-white border-y-white'>
          <div className='mb-2'></div>
          <div className='mb-4 w-full'>
            {visibleForbiddenCategories.length > 0 ? (
              <ul className='mr-4 max-h-[500px] overflow-y-auto'>
                {visibleForbiddenCategories.map((item, idx) => (
                  <li
                    key={item.id}
                    className={`w-full ${
                      item.enable == 1
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-500'
                    } font-semibold py-2 my-2 px-2 rounded-md flex justify-between items-center`}
                  >
                    <div className='flex flex-col'>
                      <span>{item.name || item.id}</span>
                      {item.name && (
                        <span className='text-xs font-normal'>{item.id}</span>
                      )}
                    </div>
                    <div className='flex'>
                      {!item?.byuser ? (
                        <select
                          id={`forbiddenCategoryOption${idx}`}
                          name={`forbiddenCategoryOption${idx}`}
                          autoComplete='forbiddenCategoryOption-name'
                          className='p-2 rounded-sm bg-white font-normal'
                          value={item.enable}
                          onChange={(e) =>
                            forbiddenCategoryOptionHandler(item.id, e.target.value)
                          }
                        >
                          <option value={0}>No Permitido</option>
                          <option value={1}>Permitido por el usuario</option>
                        </select>
                      ) : (
                        <RemoveRowButtonComponent
                          position={idx}
                          onRemoveRow={onRemoveRow}
                          iconTitle={'Remover item'}
                          name={item}
                        />
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className='w-full flex justify-center'>
                <p>No se encontraron registros</p>
              </div>
            )}
          </div>
        </div>
        <div className='w-[50%]'>
          <div className='mb-2 w-full'></div>
        </div>
      </div>
      <div className='w-full flex mt-2 justify-end'>
        <SaveButtonComponent
          onSaveSettings={onSaveSettings}
          isValidated={isValidated}
          isValidatedKg={isValidatedKg}
          loading={loading}
        />
      </div>
    </div>
  );
};

ForbiddenMlCategories.propTypes = {
  forbiddenMlCategories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      enable: PropTypes.number,
      byuser: PropTypes.number,
    })
  ).isRequired,
  setForbiddenMlCategories: PropTypes.func.isRequired,
  savedForbiddenMlCategories: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      enable: PropTypes.number,
      byuser: PropTypes.number,
    })
  ),
  onSaveSettings: PropTypes.func.isRequired,
  isValidated: PropTypes.bool,
  isValidatedKg: PropTypes.bool,
  loading: PropTypes.bool,
};

export default ForbiddenMlCategories;
